import React, { useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import './AddCandidateToSelectionModal.scss';

interface Job {
  id: number;
  title: string;
  location?: string;
  status: string;
}

interface AddCandidateToSelectionModalProps {
  cvId?: number;
  cvIds?: number[];
  candidateName: string;
  onClose: () => void;
  onSuccess?: (added: number, skipped: number) => void;
}

/**
 * AddCandidateToSelectionModal
 *
 * Adds one or more CV candidates into a job's application pipeline
 * (visible at /jobs/{id}/applications).
 */
const AddCandidateToSelectionModal: React.FC<AddCandidateToSelectionModalProps> = ({
  cvId,
  cvIds,
  candidateName,
  onClose,
  onSuccess,
}) => {
  const resolvedCvIds = cvIds && cvIds.length > 0 ? cvIds : cvId ? [cvId] : [];

  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/jobs/my/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (response.ok) {
        const result = await response.json();
        const allJobs: Job[] = result.data?.jobs || result.jobs || result.data || [];
        // Exclude closed/archived jobs
        setJobs(allJobs.filter(j => j.status !== 'closed' && j.status !== 'archived'));
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoadingJobs(false);
    }
  };

  const handleAdd = async () => {
    if (!selectedJobId || resolvedCvIds.length === 0) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('auth_token');
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

      const response = await fetch(`${API_URL}/job-applications/bulk-add`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ job_id: selectedJobId, cv_ids: resolvedCvIds }),
      });

      if (response.ok) {
        const result = await response.json();
        const added: number = result.added ?? resolvedCvIds.length;
        const skipped: number = result.skipped ?? 0;

        setSuccessMessage(
          skipped > 0
            ? `Pridėta: ${added}, praleista (jau yra): ${skipped}`
            : `Kandidatas (-ai) sėkmingai priskirtas prie skelbimo!`
        );
        setErrorMessage(null);
        setTimeout(() => setSuccessMessage(null), 3000);

        if (onSuccess) onSuccess(added, skipped);
        setSelectedJobId(null);
      } else {
        const err = await response.json().catch(() => ({}));
        setErrorMessage(err.message || 'Nepavyko pridėti kandidato');
        setSuccessMessage(null);
      }
    } catch {
      setErrorMessage('Įvyko klaida pridedant kandidatą');
      setSuccessMessage(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="add-candidate-modal" onClick={(e) => e.stopPropagation()}>
        <button className="btn-close-modal" onClick={onClose}>
          <X size={24} />
        </button>

        <h2 className="modal-title">Priskirti prie skelbimo</h2>
        <p className="candidate-name">{candidateName}</p>

        {successMessage && (
          <div className="notification success-notification">
            <Check size={20} />
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className="notification error-notification">
            <X size={20} />
            <span>{errorMessage}</span>
            <button className="btn-close-notification" onClick={() => setErrorMessage(null)}>×</button>
          </div>
        )}

        {loadingJobs ? (
          <div className="loading-state">Kraunama...</div>
        ) : (
          <div className="add-selection-form">
            <p className="section-label">Pasirinkite skelbimą, prie kurio priskirti kandidatą</p>

            <div className="form-group">
              {jobs.length === 0 ? (
                <p style={{ color: '#999', fontSize: '0.9rem', textAlign: 'center', padding: '12px 0' }}>
                  Neturite aktyvių skelbimų
                </p>
              ) : (
                <select
                  className="select-input"
                  value={selectedJobId || ''}
                  onChange={(e) => setSelectedJobId(e.target.value ? parseInt(e.target.value) : null)}
                >
                  <option value="">Pasirinkite skelbimą</option>
                  {jobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title}{job.location ? ` — ${job.location}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <button
              className="btn-add"
              onClick={handleAdd}
              disabled={!selectedJobId || loading || jobs.length === 0}
            >
              <Check size={20} />
              {loading ? 'PRIDEDAMA...' : 'ĮKELTI'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AddCandidateToSelectionModal;
